import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

const VIEWPORT_PADDING = 8
const ANCHOR_GAP = 8

export type PortalDropdownPlacement = 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start'

interface PortalDropdownMenuProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: ReactElement<{ onClick?: (event: React.MouseEvent<HTMLElement>) => void }>
  children: ReactNode
  placement?: PortalDropdownPlacement
  menuWidth?: number
  menuClassName?: string
  /** Extra classes on the trigger anchor wrapper (e.g. `w-full` for sidebar). */
  anchorClassName?: string
  'data-tour'?: string
}

function computePlacement(
  anchorRect: DOMRect,
  menuSize: { width: number; height: number },
  preferred: PortalDropdownPlacement,
): { top: number; left: number } {
  const placeAbove = preferred.startsWith('top')
  const alignEnd = preferred.endsWith('end')

  let top = placeAbove
    ? anchorRect.top - ANCHOR_GAP - menuSize.height
    : anchorRect.bottom + ANCHOR_GAP

  let left = alignEnd
    ? anchorRect.right - menuSize.width
    : anchorRect.left

  if (top < VIEWPORT_PADDING) {
    top = anchorRect.bottom + ANCHOR_GAP
  } else if (top + menuSize.height > window.innerHeight - VIEWPORT_PADDING) {
    top = anchorRect.top - ANCHOR_GAP - menuSize.height
  }

  if (left + menuSize.width > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - menuSize.width - VIEWPORT_PADDING
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING
  }

  return { top, left }
}

export default function PortalDropdownMenu({
  open,
  onOpenChange,
  trigger,
  children,
  placement = 'top-end',
  menuWidth = 192,
  menuClassName = '',
  anchorClassName = '',
  'data-tour': dataTour,
}: PortalDropdownMenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  const updatePosition = useCallback(() => {
    const anchorEl = anchorRef.current
    const menuEl = menuRef.current
    if (!anchorEl || !menuEl) return

    const anchorRect = anchorEl.getBoundingClientRect()
    const menuRect = menuEl.getBoundingClientRect()
    setPosition(
      computePlacement(anchorRect, { width: menuRect.width, height: menuRect.height }, placement),
    )
  }, [placement])

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    updatePosition()
  }, [open, updatePosition, children])

  useEffect(() => {
    if (!open) return

    const handleScrollOrResize = () => updatePosition()
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return
      onOpenChange(false)
    }

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onOpenChange])

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    trigger.props.onClick?.(event)
    if (event.defaultPrevented) return
    onOpenChange(!open)
  }

  const triggerElement = isValidElement(trigger)
    ? cloneElement(trigger, { onClick: handleTriggerClick })
    : trigger

  return (
    <>
      <div
        ref={anchorRef}
        data-tour={dataTour}
        className={['relative inline-flex shrink-0', anchorClassName].filter(Boolean).join(' ')}
      >
        {triggerElement}
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className={[
              'fixed rounded-lg shadow-lg',
              // Defaults only when caller doesn't pass chrome — avoids Tailwind
              // conflict where earlier utilities (bg-white, z-50) beat overrides.
              menuClassName || 'z-50 border border-gray-200 bg-white py-1',
            ].join(' ')}
            style={{
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: menuWidth,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}
